import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { connect } from 'react-redux';

import { addPageData, navigateWizard, persistSeasonClass } from '../../actions';
import Button from '../../components/Button';
import Loading from '../../components/Loading';
import PageLayout from '../../components/PageLayout/PageLayout';
import SmallSelectButton from '../../components/SmallSelectButton';
import { SUBMIT_DECLARATIONS } from '../../constants/heapEvents';
import analytics from '../../lib/analytics';
import { track } from '../../lib/heapHelpers';
import * as payloadFactory from '../../lib/pageDataPayloadFactory';
import { courseValidationSelector } from '../../reducers/selectors';
import './index.scss';

export const DeclarationPage = ({
  initialEverDefaultedLoan,
  initialDeclarationConfirm,
  initialCriminalRecord,
  dispatchAddPageData,
  dispatchWizardNav,
  dispatchUpdateSeasonClass,
  validatedCourse,
}) => {
  const [everDefaultedLoan, setEverDefaultedLoan] = useState(initialEverDefaultedLoan);
  const [bankruptOrInsolvent, setBankruptOrInsolvent] = useState(
    initialDeclarationConfirm.bankruptOrInsolvent,
  );
  const [criminalRecord, setCriminalRecord] = useState(initialCriminalRecord);
  const [isLoading, setIsLoading] = useState(false);

  const isInvalid =
    everDefaultedLoan === null || bankruptOrInsolvent === null || criminalRecord === null;

  const handleClick = (category, event) => {
    switch (category) {
      case 'defaultedLoan': {
        track('Click - Ever defaulted on a loan (Declaration Page)', {
          'Value selected': event.target.value,
        });
        return setEverDefaultedLoan(event.target.value === 'true');
      }
      case 'declaredBankrupt': {
        track('Click - Declared bankrupt or insolvent (Declaration Page)', {
          'Value selected': event.target.value,
        });
        return setBankruptOrInsolvent(event.target.value === 'true');
      }
      case 'criminalRecord': {
        track('Click - Criminal record (Declaration Page)', {
          'Value selected': event.target.value,
        });
        return setCriminalRecord(event.target.value === 'true');
      }
      default: {
        return null;
      }
    }
  };

  const handleSubmit = () => {
    analytics.sendEvent('Click - Next', 'declaration');
    track(SUBMIT_DECLARATIONS, {
      'Ever defaultd on a loan': everDefaultedLoan,
      'Been bankrupt or insolvent': bankruptOrInsolvent,
      'Have a criminal record': criminalRecord,
    });

    const loanDeafultPayload = payloadFactory.createAddPageDataPayload(
      'everDefaultedLoan',
      everDefaultedLoan,
    );
    const criminalRecordPayload = payloadFactory.createAddPageDataPayload(
      'criminalRecord',
      criminalRecord,
    );
    const bankruptOrInsolventPayload = payloadFactory.createAddPageDataPayload(
      'declarationConfirm',
      { bankruptOrInsolvent: bankruptOrInsolvent },
    );
    dispatchAddPageData(loanDeafultPayload);
    dispatchAddPageData(criminalRecordPayload);
    dispatchAddPageData(bankruptOrInsolventPayload);

    setIsLoading(true);
    if (validatedCourse === false) {
      dispatchUpdateSeasonClass();
    } else {
      dispatchWizardNav({ dir: 'fwd' });
    }
  };

  return (
    <div>
      {!isLoading && (
        <PageLayout title="Are there any prior records we should be aware of?">
          <div>
            <p>
              We understand that sometimes there are situations beyond your control. Please share
              with us and our committee will review your case accordingly.
            </p>
            <div className="declaration-section">
              <div className="text-heavy">Have you ever defaulted on a loan?</div>
              <SmallSelectButton
                selectButtonId="defaulted-loan"
                defaultCheckedButton={everDefaultedLoan}
                handleClick={e => handleClick('defaultedLoan', e)}
              />
            </div>
            <div className="declaration-section">
              <div className="text-heavy">Have you ever been declared bankrupt or insolvent?</div>
              <SmallSelectButton
                selectButtonId="declared-bankrupt"
                defaultCheckedButton={bankruptOrInsolvent}
                handleClick={e => handleClick('declaredBankrupt', e)}
              />
            </div>
            <div className="declaration-section">
              <div className="text-heavy">Do you have a criminal record?</div>
              <SmallSelectButton
                selectButtonId="criminal-record"
                defaultCheckedButton={criminalRecord}
                handleClick={e => handleClick('criminalRecord', e)}
              />
            </div>
          </div>
          <div className="row-spacer margin-top">
            <Button type="submit" isDisabled={isInvalid} onClick={handleSubmit}>
              Submit
            </Button>
          </div>
        </PageLayout>
      )}
      {isLoading && (
        <section className="container container--space-y text-center">
          <Loading />
        </section>
      )}
    </div>
  );
};

DeclarationPage.propTypes = {
  initialEverDefaultedLoan: PropTypes.bool,
  initialDeclarationConfirm: PropTypes.object,
  initialCriminalRecord: PropTypes.bool,
  dispatchAddPageData: PropTypes.func.isRequired,
  dispatchWizardNav: PropTypes.func.isRequired,
  validatedCourse: PropTypes.bool.isRequired,
  dispatchUpdateSeasonClass: PropTypes.func.isRequired,
};

DeclarationPage.defaultProps = {
  initialEverDefaultedLoan: null,
  initialDeclarationConfirm: { bankruptOrInsolvent: null },
  initialCriminalRecord: null,
};

export const mapStateToProps = reduxState => ({
  initialEverDefaultedLoan: reduxState.builder.ui.everDefaultedLoan,
  initialDeclarationConfirm: reduxState.builder.ui.declarationConfirm,
  initialCriminalRecord: reduxState.builder.ui.criminalRecord,
  validatedCourse: courseValidationSelector(reduxState),
});

export const mapDispatchToProps = {
  dispatchAddPageData: addPageData,
  dispatchWizardNav: navigateWizard,
  dispatchUpdateSeasonClass: persistSeasonClass,
};

export default connect(mapStateToProps, mapDispatchToProps)(DeclarationPage);
